import React, { useState, useEffect, useCallback } from 'react';
import '../shared.css';
import './Booking.css';
import Sidebar from '../Components/Layout/sidebar';
import Topbar from '../Components/Layout/topbar';
import Button from '../Components/Comman/Button';
import Analytic_cards from '../Components/Comman/Analytics_card';
import BookingTable, { StatusBadge } from '../Components/Layout/Bookingtabel';
import Modal from '../Components/Comman/Modal';
import {
  FieldGroup, TextInput, SelectInput, FormGrid, FormActions, DeleteConfirm,
} from '../Components/Comman/FormFields';
import { supabase } from '../supabaseClient';

/* Actual DB columns: id, booked_at, booking_settings_id, user_id,
   ticket_id, ticket_code, quantity, total_price, payment_status      */

const BOOKING_COLUMNS = [
  { key: 'ticket_code',    label: 'Ticket Code' },
  { key: 'quantity',       label: 'Qty' },
  { key: 'total_price',    label: 'Total (EGP)', render: (val) => val != null ? `EGP ${Number(val).toLocaleString()}` : '—' },
  { key: 'payment_status', label: 'Payment',     render: (val) => <StatusBadge value={val ? 'Confirmed' : 'Pending'} /> },
  { key: 'booked_at',      label: 'Booked At',   render: (val) => val ? new Date(val).toLocaleString('en-GB', { dateStyle: 'medium', timeStyle: 'short' }) : '—' },
  { key: '__actions',      label: 'Actions',
    render: (_, row, ctx) => (
      <div style={{ display: 'flex', gap: 6 }}>
        <ActionBtn label="Edit"   onClick={() => ctx.onEdit(row)}   />
        <ActionBtn label="Delete" onClick={() => ctx.onDelete(row)} danger />
      </div>
    )
  },
];

const ActionBtn = ({ label, onClick, danger }) => (
  <button type="button" onClick={onClick}
    style={{
      background: danger ? 'rgba(180,60,60,0.08)' : 'none',
      border: `0.762px solid ${danger ? 'rgba(180,60,60,0.25)' : 'rgba(42,42,42,0.2)'}`,
      borderRadius: 5, padding: '4px 10px', fontFamily: 'Inter',
      fontSize: 11.5, cursor: 'pointer', color: danger ? '#a33' : '#191919',
      transition: 'background 0.15s',
    }}
    onMouseEnter={e => e.currentTarget.style.background = danger ? 'rgba(180,60,60,0.15)' : 'rgba(214,184,147,0.3)'}
    onMouseLeave={e => e.currentTarget.style.background = danger ? 'rgba(180,60,60,0.08)' : 'none'}
  >{label}</button>
);

const blank = () => ({
  ticket_code: '', quantity: 1, total_price: 0, payment_status: false,
});

const BookingForm = ({ initial, onSave, onCancel, loading }) => {
  const [form, setForm] = useState(initial || blank());
  useEffect(() => { setForm(initial || blank()); }, [initial]);
  const set = (key, asNumber = false) => (val) =>
    setForm(f => ({ ...f, [key]: asNumber ? Number(val) : val }));
  const handleSubmit = (e) => { e.preventDefault(); onSave(form); };

  return (
    <form onSubmit={handleSubmit}>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        <FormGrid>
          <FieldGroup label="Ticket Code" required>
            <TextInput value={form.ticket_code} onChange={set('ticket_code')} placeholder="e.g. 123456789" required />
          </FieldGroup>
          <FieldGroup label="Quantity" required>
            <TextInput value={form.quantity} onChange={set('quantity', true)} type="number" required />
          </FieldGroup>
          <FieldGroup label="Total Price (EGP)" required>
            <TextInput value={form.total_price} onChange={set('total_price', true)} type="number" required />
          </FieldGroup>
          <FieldGroup label="Payment Status">
            <SelectInput
              value={form.payment_status === true ? 'Paid' : 'Unpaid'}
              onChange={(val) => setForm(f => ({ ...f, payment_status: val === 'Paid' }))}
              options={['Paid', 'Unpaid']}
            />
          </FieldGroup>
        </FormGrid>
        <FormActions onCancel={onCancel} submitLabel={initial?.id ? 'Update Booking' : 'Create Booking'} loading={loading} />
      </div>
    </form>
  );
};

const Booking_page = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading]   = useState(false);
  const [saving, setSaving]     = useState(false);
  const [modal, setModal]       = useState(null);
  const [selected, setSelected] = useState(null);
  const [toast, setToast]       = useState(null);

  const showToast = (msg, isError = false) => {
    setToast({ msg, isError });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchBookings = useCallback(async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from('bookings')
      .select('*')
      .order('booked_at', { ascending: false });
    if (error) showToast('Failed to load bookings: ' + error.message, true);
    else setBookings(data || []);
    setLoading(false);
  }, []);

  useEffect(() => { fetchBookings(); }, [fetchBookings]);

  const tableCtx = {
    onEdit:   (row) => { setSelected(row); setModal('edit');   },
    onDelete: (row) => { setSelected(row); setModal('delete'); },
  };

  const columns = BOOKING_COLUMNS.map(col =>
    col.key === '__actions'
      ? { ...col, render: (val, row) => col.render(val, row, tableCtx) }
      : col
  );

  const handleCreate = async (form) => {
    setSaving(true);
    const payload = { ...form };
    delete payload.id;
    payload.booked_at = new Date().toISOString();
    const { error } = await supabase.from('bookings').insert([payload]);
    setSaving(false);
    if (error) { showToast('Error creating booking: ' + error.message, true); return; }
    showToast('Booking created ✓');
    setModal(null);
    fetchBookings();
  };

  const handleUpdate = async (form) => {
    setSaving(true);
    const { id, booked_at, booking_settings_id, user_id, ticket_id, ...rest } = form;
    const { error } = await supabase.from('bookings').update(rest).eq('id', id);
    setSaving(false);
    if (error) { showToast('Error updating booking: ' + error.message, true); return; }
    showToast('Booking updated ✓');
    setModal(null);
    fetchBookings();
  };

  const handleDelete = async () => {
    setSaving(true);
    const { error } = await supabase.from('bookings').delete().eq('id', selected.id);
    setSaving(false);
    if (error) { showToast('Error deleting: ' + error.message, true); return; }
    showToast('Booking deleted ✓');
    setModal(null);
    fetchBookings();
  };

  const closeModal = () => { setModal(null); setSelected(null); };
  const paid   = bookings.filter(b => b.payment_status === true).length;
  const unpaid = bookings.filter(b => !b.payment_status).length;

  return (
    <main>
      <Sidebar />
      <div className='pagestrcutre'>
        <Topbar />

        {toast && (
          <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 99999, background: toast.isError ? '#a33' : '#2d7a2d', color: '#fff', padding: '12px 20px', borderRadius: 8, fontFamily: 'Inter', fontSize: 13, boxShadow: '0 6px 20px rgba(0,0,0,0.2)' }}>{toast.msg}</div>
        )}

        <div className='pagetop_div'>
          <div className='pagetop_div1'>
            <h3 className='pagetop_text1'>Bookings Manager</h3>
            <Button text='+ New Booking' icon={null} onClick={() => { setSelected(null); setModal('create'); }} />
          </div>
          <p className='pagetop_text2'>Manage all exhibition bookings</p>
        </div>

        <div className='analytics_div'>
          <Analytic_cards text1='Total Bookings' text2={bookings.length} text3='All records' />
          <Analytic_cards text1='Paid'           text2={paid}            text3='Payment confirmed' />
          <Analytic_cards text1='Unpaid'         text2={unpaid}          text3='Awaiting payment' />
        </div>

        <div className='page_content'>
          {loading
            ? <p className='pagetop_text2'>Loading bookings…</p>
            : <BookingTable columns={columns} data={bookings} emptyText='No bookings found.' />
          }
        </div>

        <Modal open={modal === 'create'} onClose={closeModal} title="New Booking">
          <BookingForm onSave={handleCreate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'edit'} onClose={closeModal} title="Edit Booking">
          <BookingForm initial={selected} onSave={handleUpdate} onCancel={closeModal} loading={saving} />
        </Modal>
        <Modal open={modal === 'delete'} onClose={closeModal} title="Delete Booking" width={420}>
          <DeleteConfirm label={selected?.ticket_code} onConfirm={handleDelete} onCancel={closeModal} loading={saving} />
        </Modal>
      </div>
    </main>
  );
};

export default Booking_page;